
/* ============================== src/components/discussion/CommentComposer.jsx ============================== */
// src/components/discussion/CommentComposer.jsx
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Ic } from "../Icons";
import { ICONS } from "../iconsData";
import MentionPopup from "./MentionPopup";
import { useMentions } from "../../hooks/useMentions";
import { UPLOAD_ACCEPT } from "../../utils/constants";
import { validateAttachment } from "../../utils/fileValidation";
import { useAddCommentMutation } from "../../api/queries/comments";
import { useUploadFileMutation } from "../../api/queries/files";

const REPLY_PREFIX_RE = /^@?[^\s,]+\s+[^\s,]+,\s*/;

function CommentComposer({
  projectId = null,
  taskId = null,
  candidates,
  toast,
  replyTo,
  setReplyTo,
  comments,
  getAuthor,
  onCommentCreated,
}) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isBatchUploading, setIsBatchUploading] = useState(false);

  const [draftId, setDraftId] = useState(() => crypto.randomUUID());

  const sendingRef = useRef(false);
  const batchUploadingRef = useRef(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const addMutation = useAddCommentMutation();
  // Отключаем инвалидацию кэша при загрузке файлов, чтобы избежать rate-limit (429)
  const uploadMutation = useUploadFileMutation({ invalidate: false });

  const mentions = useMentions({ text, setText, candidates, textareaRef });

  const [prevReplyTo, setPrevReplyTo] = useState(null);
  if (replyTo !== prevReplyTo) {
    setPrevReplyTo(replyTo);
    if (replyTo) {
      const parent = comments.find((c) => c.id === replyTo);
      const author = parent ? getAuthor(parent.authorId) : null;
      if (author) {
        const prefix = `@${author.last} ${author.first}, `;
        setText((prev) => prefix + prev.replace(REPLY_PREFIX_RE, ""));
      }
    }
  }

  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.setProperty("--textarea-h", "auto");
    void ta.offsetHeight;
    const borderY = ta.offsetHeight - ta.clientHeight;
    ta.style.setProperty("--textarea-h", `${ta.scrollHeight + borderY}px`);
  }, [text]);

  useLayoutEffect(() => {
    if (!replyTo) return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
  }, [replyTo]);

  const cancelReply = useCallback(() => {
    setReplyTo(null);
    setText((prev) => prev.replace(REPLY_PREFIX_RE, ""));
  }, [setReplyTo]);

  const handleFiles = useCallback(
    async (files) => {
      if (batchUploadingRef.current) return;
      batchUploadingRef.current = true;
      setIsBatchUploading(true);
      try {
        const valid = [];
        const rejected = [];
        for (const f of files) {
          const v = validateAttachment(f);
          if (v.ok) valid.push(f);
          else rejected.push({ name: f.name, reason: v.reason });
        }

        if (rejected.length) {
          const first = rejected[0];
          const more =
            rejected.length > 1 ? ` (и ещё ${rejected.length - 1})` : "";
          toast?.(`${first.name}: ${first.reason}${more}`, "warning");
        }

        for (const file of valid) {
          const tempId = crypto.randomUUID();
          setUploadProgress((prev) => ({
            ...prev,
            [tempId]: { name: file.name, progress: 0 },
          }));
          try {
            const uploadedFile = await uploadMutation.mutateAsync({
              file,
              ownerType: "comment",
              ownerId: draftId,
              taskId: taskId || undefined,
              projectId: projectId || undefined,
              onUploadProgress: (e) => {
                if (!e.total) return;
                const pct = Math.round((e.loaded / e.total) * 100);
                setUploadProgress((prev) =>
                  prev[tempId]
                    ? { ...prev, [tempId]: { ...prev[tempId], progress: pct } }
                    : prev,
                );
              },
            });
            setAttachments((prev) => [...prev, uploadedFile]);
          } catch (err) {
            toast?.(err.message || "Ошибка загрузки файла", "error");
          } finally {
            setUploadProgress((prev) => {
              if (!prev[tempId]) return prev;
              const next = { ...prev };
              delete next[tempId];
              return next;
            });
          }
        }
      } finally {
        batchUploadingRef.current = false;
        setIsBatchUploading(false);
      }
    },
    [draftId, taskId, projectId, uploadMutation, toast],
  );

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(Array.from(e.dataTransfer.files));
    },
    [handleFiles],
  );

  const hasActiveUploads =
    uploadMutation.isPending || Object.keys(uploadProgress).length > 0;
  const isBusy = isSending || isBatchUploading || hasActiveUploads;

  const send = async () => {
    if (sendingRef.current) return;
    if (isBusy) {
      toast?.("Дождитесь загрузки файлов", "warning");
      return;
    }
    if (!text.trim() && attachments.length === 0) {
      toast?.("Введите текст или прикрепите файл", "warning");
      return;
    }

    sendingRef.current = true;
    setIsSending(true);
    try {
      const created = await addMutation.mutateAsync({
        id: draftId,
        projectId: projectId || undefined,
        taskId: taskId || undefined,
        parentId: replyTo || undefined,
        text: text.trim(),
      });
      if (created?.id) onCommentCreated?.(created.id);

      setText("");
      setReplyTo(null);
      setAttachments([]);
      setDraftId(crypto.randomUUID());
      mentions.reset();
      toast?.("Комментарий добавлен", "success");
    } catch (err) {
      toast?.(err.message || "Ошибка отправки комментария", "error");
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const { selectionStart: start, selectionEnd: end } = e.target;
      setText(text.substring(0, start) + "\n" + text.substring(end));
      requestAnimationFrame(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 1;
      });
      return;
    }
    e.preventDefault();
    if (sendingRef.current) return;
    send();
  };

  const parent = replyTo ? comments.find((x) => x.id === replyTo) : null;
  const parentAuthor = parent ? getAuthor(parent.authorId) : null;

  return (
    <div className="cm-composer">
      {replyTo && (
        <div className="reply-banner">
          Ответ на комментарий{" "}
          {parentAuthor ? `${parentAuthor.last} ${parentAuthor.first}` : ""}
          <button className="link" onClick={cancelReply}>
            отменить
          </button>
        </div>
      )}

      <div
        className={`cm-input-wrap${isDragOver ? " drag-over" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <MentionPopup
          popup={mentions.popup}
          candidates={mentions.filteredCandidates}
          onPick={mentions.pick}
        />
        <textarea
          ref={textareaRef}
          className="inp"
          rows="2"
          placeholder="Комментарий… Введите @ для упоминания участника. Перетащите файлы сюда."
          value={text}
          onChange={(e) => mentions.onType(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </div>

      {Object.entries(uploadProgress).length > 0 && (
        <div className="cm-attachments">
          {Object.entries(uploadProgress).map(([id, item]) => (
            <div className="cm-attachment-chip" key={id}>
              <span className="text-sm">
                {item.name} — {item.progress}%
              </span>
            </div>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="cm-attachments">
          {attachments.map((file) => (
            <div className="cm-attachment-chip" key={file.id}>
              <span className="text-sm">{file.name}</span>
              <button
                className="icon-btn xs"
                onClick={() =>
                  setAttachments((prev) => prev.filter((f) => f.id !== file.id))
                }
                disabled={isBusy}
                aria-label={`Убрать ${file.name}`}
              >
                <Ic d={ICONS.x} size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="cm-foot">
        <span className="mut sm">
          Участники получат уведомление; упомянутые - отдельно.
        </span>
        <div className="flex gap-2">
          <input
            type="file"
            className="file-input-hidden"
            multiple
            ref={fileInputRef}
            accept={UPLOAD_ACCEPT}
            onChange={(e) => {
              handleFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy}
          >
            <Ic d={ICONS.file} size={13} />{" "}
            {isBatchUploading || hasActiveUploads ? "Загрузка…" : "Загрузить"}
          </button>

          <button className="btn primary sm" onClick={send} disabled={isBusy}>
            <Ic d={ICONS.chat} size={13} />{" "}
            {isSending ? "Отправка…" : "Отправить"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CommentComposer;
export { CommentComposer };
